import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import {
  TrashIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

const Congregazioni = () => {
  const { user, activeCongregazione, setActiveCongregazione } = useAuth();
  const [congregazioni, setCongregazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nome, setNome] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [congregazioneToDelete, setCongregazioneToDelete] = useState(null);
  const [servizioSpeciale, setServizioSpeciale] = useState(false);
  const [updatingSpecialId, setUpdatingSpecialId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [updatingNameId, setUpdatingNameId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 15;

  useEffect(() => {
    const loadCongregazioni = async () => {
      if (user?.ruolo !== "super_admin") {
        setCongregazioni([]);
        setLoading(false);
        return;
      }

      try {
        const response = await api.get("/congregazioni");
        setCongregazioni(response.data);
      } catch (error) {
        console.error("Errore nel caricamento congregazioni:", error);
        toast.error("Impossibile caricare le congregazioni");
      } finally {
        setLoading(false);
      }
    };

    loadCongregazioni();
  }, [user]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredCongregazioni = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) {
      return congregazioni;
    }
    return congregazioni.filter((congregazione) =>
      (congregazione.nome || "").toLowerCase().includes(normalized)
    );
  }, [congregazioni, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCongregazioni.length / pageSize)
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedCongregazioni = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCongregazioni.slice(start, start + pageSize);
  }, [currentPage, filteredCongregazioni, pageSize]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast.error("Inserisci un nome per la congregazione");
      return;
    }

    try {
      setCreating(true);
      const response = await api.post("/congregazioni", {
        nome: nome.trim(),
        servizio_speciale: servizioSpeciale,
      });

      setCongregazioni((prev) => [...prev, response.data]);
      toast.success("Congregazione creata con successo");
      setNome("");
      setServizioSpeciale(false);
    } catch (error) {
      console.error("Errore creazione congregazione:", error);
      const message =
        error.response?.data?.message || "Impossibile creare la congregazione";
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleSelectActive = (congregazione) => {
    setActiveCongregazione({
      id: congregazione.id,
      codice: congregazione.codice,
      nome: congregazione.nome,
      servizio_speciale: congregazione.servizio_speciale,
      rapporto_servizio_attivo: congregazione.rapporto_servizio_attivo,
      gruppi_servizio_visibili: congregazione.gruppi_servizio_visibili,
    });
    toast.success(
      `Congregazione attiva impostata: ${congregazione.codice} - ${congregazione.nome}`
    );
  };

  const handleEditStart = (congregazione) => {
    setEditingId(congregazione.id);
    setEditingName(congregazione.nome || "");
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleEditSave = async (congregazione) => {
    const trimmedName = editingName.trim();
    if (!trimmedName) {
      toast.error("Inserisci un nome per la congregazione");
      return;
    }

    if (trimmedName === congregazione.nome) {
      handleEditCancel();
      return;
    }

    try {
      setUpdatingNameId(congregazione.id);
      const response = await api.put(`/congregazioni/${congregazione.id}`, {
        nome: trimmedName,
      });

      setCongregazioni((prev) =>
        prev.map((c) =>
          c.id === congregazione.id ? { ...c, ...response.data } : c
        )
      );

      if (activeCongregazione?.id === congregazione.id) {
        setActiveCongregazione({
          id: response.data.id,
          codice: response.data.codice,
          nome: response.data.nome,
          servizio_speciale: response.data.servizio_speciale,
          rapporto_servizio_attivo: response.data.rapporto_servizio_attivo,
          gruppi_servizio_visibili: response.data.gruppi_servizio_visibili,
        });
      }

      toast.success("Nome congregazione aggiornato");
      handleEditCancel();
    } catch (error) {
      console.error("Errore aggiornamento congregazione:", error);
      const message =
        error.response?.data?.message ||
        "Impossibile aggiornare la congregazione";
      toast.error(message);
    } finally {
      setUpdatingNameId(null);
    }
  };

  const handleDeleteClick = (congregazione) => {
    setCongregazioneToDelete(congregazione);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!congregazioneToDelete) return;

    try {
      setDeletingId(congregazioneToDelete.id);
      await api.delete(`/congregazioni/${congregazioneToDelete.id}`);

      // Se la congregazione eliminata era quella attiva, rimuovila
      if (activeCongregazione?.id === congregazioneToDelete.id) {
        setActiveCongregazione(null);
      }

      // Rimuovi dalla lista
      setCongregazioni((prev) =>
        prev.filter((c) => c.id !== congregazioneToDelete.id)
      );

      toast.success(
        `Congregazione ${congregazioneToDelete.codice} - ${congregazioneToDelete.nome} eliminata con successo`
      );
      setShowDeleteModal(false);
      setCongregazioneToDelete(null);
    } catch (error) {
      console.error("Errore eliminazione congregazione:", error);
      const message =
        error.response?.data?.message ||
        "Impossibile eliminare la congregazione";
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setCongregazioneToDelete(null);
  };

  const handleToggleServizioSpeciale = async (congregazione) => {
    try {
      setUpdatingSpecialId(congregazione.id);
      const response = await api.put(`/congregazioni/${congregazione.id}`, {
        nome: congregazione.nome,
        servizio_speciale: !congregazione.servizio_speciale,
      });

      setCongregazioni((prev) =>
        prev.map((c) =>
          c.id === congregazione.id ? { ...c, ...response.data } : c
        )
      );

      if (activeCongregazione?.id === congregazione.id) {
        setActiveCongregazione({
          id: response.data.id,
          codice: response.data.codice,
          nome: response.data.nome,
          servizio_speciale: response.data.servizio_speciale,
          rapporto_servizio_attivo: response.data.rapporto_servizio_attivo,
          gruppi_servizio_visibili: response.data.gruppi_servizio_visibili,
        });
      }

      toast.success(
        response.data.servizio_speciale
          ? "Servizio speciale attivato per la congregazione"
          : "Servizio speciale disattivato"
      );
    } catch (error) {
      console.error("Errore aggiornamento servizio speciale:", error);
      const message =
        error.response?.data?.message ||
        "Impossibile aggiornare il servizio speciale";
      toast.error(message);
    } finally {
      setUpdatingSpecialId(null);
    }
  };

  if (user?.ruolo !== "super_admin") {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900">Congregazioni</h1>
        <p className="mt-2 text-gray-600">
          Solo il SuperAdmin può accedere alla gestione delle congregazioni.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 w-1/4 rounded" />
          <div className="h-20 bg-gray-200 rounded" />
          <div className="h-20 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gestione Congregazioni</h1>
        <p className="text-gray-600 mt-1">
          Crea nuove congregazioni, imposta quella attiva e visualizza l’elenco
          completo.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Congregazione attiva
        </h2>
        {activeCongregazione?.id ? (
          <p className="mt-2 text-gray-700">
            <span className="font-medium">{activeCongregazione.codice}</span> ·{" "}
            {activeCongregazione.nome}
          </p>
        ) : (
          <p className="mt-2 text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-3">
            Nessuna congregazione attiva selezionata. Scegline una dall’elenco
            per operare sulle relative postazioni, volontari e turni.
          </p>
        )}
      </div>

      <form
        onSubmit={handleCreate}
        className="bg-white shadow rounded-lg p-6 space-y-4"
      >
        <div>
          <label
            htmlFor="nome"
            className="block text-sm font-medium text-gray-700"
          >
            Nome nuova congregazione
          </label>
          <input
            id="nome"
            name="nome"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
            placeholder="Es. Palermo Uditore"
          />
        </div>
        <div className="flex items-center">
          <input
            id="servizio-speciale"
            type="checkbox"
            checked={servizioSpeciale}
            onChange={(e) => setServizioSpeciale(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label
            htmlFor="servizio-speciale"
            className="ml-2 block text-sm text-gray-700"
          >
            Contrassegna come Servizio Speciale
          </label>
        </div>
        <button
          type="submit"
          disabled={creating}
          className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {creating ? "Creazione..." : "Crea Congregazione"}
        </button>
      </form>

      <div className="bg-white shadow rounded-lg">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Elenco congregazioni
          </h2>
          <p className="text-sm text-gray-600">
            Seleziona una congregazione per impostarla come attiva e lavorare sui
            relativi dati.
          </p>
        </div>
        <div className="px-6 pb-4 pt-4">
          <label className="form-label">Cerca congregazione</label>
          <div className="relative">
            <input
              type="text"
              className="form-input pl-10"
              placeholder="Nome congregazione..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Risultati: {filteredCongregazioni.length}
          </p>
        </div>
        <ul className="divide-y divide-gray-200">
          {paginatedCongregazioni.map((congregazione) => {
            const isActive = activeCongregazione?.id === congregazione.id;
            const isEditing = editingId === congregazione.id;
            const isSavingName = updatingNameId === congregazione.id;
            return (
              <li
                key={congregazione.id}
                className="p-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
                      <span className="text-sm font-medium text-gray-900">
                        {congregazione.codice} ·
                      </span>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="text-sm w-full sm:w-64 rounded-md border border-gray-300 px-2 py-1 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                        placeholder="Nome congregazione"
                        disabled={isSavingName}
                      />
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <button
                          type="button"
                          onClick={() => handleEditSave(congregazione)}
                          disabled={isSavingName}
                          className="inline-flex items-center justify-center rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isSavingName ? "Salvataggio..." : "Salva"}
                        </button>
                        <button
                          type="button"
                          onClick={handleEditCancel}
                          disabled={isSavingName}
                          className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-gray-900">
                      {congregazione.codice} · {congregazione.nome}
                    </p>
                  )}
                  <div className="mt-1 flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                        congregazione.servizio_speciale
                          ? "bg-purple-100 text-purple-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {congregazione.servizio_speciale
                        ? "Servizio Speciale"
                        : "Standard"}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleToggleServizioSpeciale(congregazione)}
                      disabled={updatingSpecialId === congregazione.id}
                      className="text-xs text-primary-600 hover:text-primary-800 disabled:opacity-50"
                    >
                      {congregazione.servizio_speciale
                        ? "Disattiva"
                        : "Attiva"}{" "}
                      servizio speciale
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Creata il {new Date(congregazione.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                  <button
                    type="button"
                    onClick={() => handleSelectActive(congregazione)}
                    className={`inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors ${
                      isActive
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-primary-600 border-primary-600 hover:bg-primary-50"
                    }`}
                  >
                    {isActive ? "Congregazione attiva" : "Imposta come attiva"}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      isEditing
                        ? handleEditCancel()
                        : handleEditStart(congregazione)
                    }
                    disabled={isSavingName}
                    className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEditing ? "Annulla modifica" : "Modifica"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(congregazione)}
                    disabled={deletingId === congregazione.id}
                    className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-red-600 border border-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Elimina
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        {filteredCongregazioni.length === 0 && (
          <div className="p-6 text-center text-gray-500">
            Nessuna congregazione trovata.
          </div>
        )}
        {filteredCongregazioni.length > 0 && totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">
              Pagina {currentPage} di {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Successiva
              </button>
            </div>
          </div>
        )}
        
        {/* Modale di conferma eliminazione */}
        {showDeleteModal && congregazioneToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Conferma eliminazione
                  </h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Stai per eliminare la congregazione:
                </p>
                <p className="text-base font-medium text-gray-900 mb-4">
                  {congregazioneToDelete.codice} · {congregazioneToDelete.nome}
                </p>
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-red-800">
                    <strong>Attenzione:</strong> La cancellazione comporterà la
                    perdita di tutti i dati associati a questa congregazione
                    (volontari, postazioni, turni, disponibilità, ecc.).
                  </p>
                  <p className="text-sm text-red-700 mt-2 font-medium">
                    L'azione è irreversibile. Vuoi procedere?
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleDeleteCancel}
                    disabled={deletingId !== null}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteConfirm}
                    disabled={deletingId !== null}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deletingId !== null ? "Eliminazione..." : "Elimina"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Congregazioni;
